import type { ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PackageWithFeatureCodes } from "../../api/packages.service";
import { packages as packagesApi } from "../../api/packages.service";
import { ROUTES } from "../../routes";
import styles from "./PackageVersionModal.module.css";
import { Step1ImpactWarning } from "./steps/Step1_ImpactWarning";
import { Step2FeatureSelection } from "./steps/Step2_FeatureSelection";
import { Step3DiffConfirmation } from "./steps/Step3_DiffConfirmation";

export type PackageVersionModalProps = {
  package: PackageWithFeatureCodes | null;
  isOpen: boolean;
  onClose: () => void;
  setToast: (toast: { message: string; type: ToastType } | null) => void;
};

type Step = "impact" | "features" | "confirmation";

export function PackageVersionModal({
  package: pkg,
  isOpen,
  onClose,
  setToast,
}: PackageVersionModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>("impact");
  const [selectedFeatureCodes, setSelectedFeatureCodes] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen && pkg) {
      setCurrentStep("impact");
      setSelectedFeatureCodes(pkg.featureCodes || []);
      setError(null);
      setIsCreating(false);
    }
  }, [isOpen, pkg]);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isCreating) {
        handleClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isCreating]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen || !pkg) return null;

  function handleClose() {
    setCurrentStep("impact");
    setSelectedFeatureCodes(pkg?.featureCodes || []);
    setError(null);
    setIsCreating(false);
    onClose();
  }

  function handleContinueToFeatures() {
    setCurrentStep("features");
  }

  function handleReviewChanges(newFeatureCodes: string[]) {
    setSelectedFeatureCodes(newFeatureCodes);
    setError(null);
    setCurrentStep("confirmation");
  }

  function handleBackToEdit() {
    setError(null);
    setCurrentStep("features");
  }

  async function handleCreateVersion() {
    if (!pkg) return;

    const startTime = Date.now();
    setIsCreating(true);
    setError(null);

    try {
      const featuresChanged =
        JSON.stringify([...(pkg.featureCodes || [])].sort()) !==
        JSON.stringify([...selectedFeatureCodes].sort());

      const response = await packagesApi.version(pkg.id, {
        featureCodes: featuresChanged ? selectedFeatureCodes : undefined,
      });

      const elapsed = Date.now() - startTime;
      const minDisplayTime = 400;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      loadingTimeoutRef.current = setTimeout(() => {
        handleClose();

        const warningText =
          response.warnings && response.warnings.length > 0
            ? `\n\nWarnings:\n${response.warnings.map(w => `• ${w.message}`).join("\n")}`
            : "";

        setToast({
          message: `Version ${response.package.version} created successfully${warningText}`,
          type: response.warnings && response.warnings.length > 0 ? "warning" : "success",
        });
        navigate(ROUTES.fulfilment.packageDetail(response.package.id), {
          state: { editMode: true, justVersioned: true },
        });
      }, remainingTime);
    } catch (err) {
      const elapsed = Date.now() - startTime;
      const minDisplayTime = 400;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      loadingTimeoutRef.current = setTimeout(() => {
        let errorMessage = "Failed to create version";

        if (err instanceof Error) {
          errorMessage = err.message;

          const error = err as Error & {
            code?: string;
            details?: Array<{
              code: string;
              path: (string | number)[];
              message: string;
            }>;
          };

          if (error.code === "VALIDATION_ERROR" && error.details && error.details.length > 0) {
            // Check for specific "too_small" error (empty array)
            const featureError = error.details.find(
              d => d.path.includes("featureCodes") && d.code === "too_small",
            );

            if (featureError) {
              errorMessage =
                "At least one feature is required. Packages must include features that define their capabilities. Please select at least one feature to continue.";
            } else {
              // Use first error detail's message for any other validation error
              const firstError = error.details[0];
              if (firstError?.message) {
                errorMessage = firstError.message;
              }
            }
          }
        }

        setError(errorMessage);
        setIsCreating(false);
      }, remainingTime);
    }
  }

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isCreating) {
      handleClose();
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="version-package-title"
      >
        <div
          className={`${styles.stepContent} ${currentStep === "impact" ? styles.stepActive : styles.stepInactive}`}
        >
          <Step1ImpactWarning
            package={pkg}
            onContinue={handleContinueToFeatures}
            onCancel={handleClose}
          />
        </div>

        <div
          className={`${styles.stepContent} ${currentStep === "features" ? styles.stepActive : styles.stepInactive}`}
        >
          <Step2FeatureSelection
            package={pkg}
            initialFeatureCodes={selectedFeatureCodes}
            onReviewChanges={handleReviewChanges}
            onCancel={handleClose}
          />
        </div>

        <div
          className={`${styles.stepContent} ${currentStep === "confirmation" ? styles.stepActive : styles.stepInactive}`}
        >
          <Step3DiffConfirmation
            package={pkg}
            selectedFeatureCodes={selectedFeatureCodes}
            onCreateVersion={handleCreateVersion}
            onBackToEdit={handleBackToEdit}
            onCancel={handleClose}
            isCreating={isCreating}
            error={error}
          />
        </div>
      </div>
    </div>
  );
}
